/**
 * Streaming Avatar Context
 * Provides state management for HeyGen avatar streaming session
 * Ported from react-migration with adaptations for TheyChat
 */

import {
  ConnectionQuality,
  type LiveAvatarSession,
  SessionState,
} from '@heygen/liveavatar-web-sdk';
import type React from 'react';
import { createContext, type ReactNode, useContext, useRef, useState } from 'react';

// Message sender types
export enum MessageSender {
  CLIENT = 'CLIENT',
  AVATAR = 'AVATAR',
}

// Message interface
export interface Message {
  id: string;
  sender: MessageSender;
  content: string;
  timestamp: string;
}

// Session close reason types
export type SessionCloseReason = 'user' | 'idle_timeout' | 'credit_limit' | 'unexpected' | null;

// Context props interface
interface StreamingAvatarContextProps {
  // References
  avatarRef: React.MutableRefObject<LiveAvatarSession | null>;
  basePath?: string;

  // Mute state
  isMuted: boolean;
  setIsMuted: (isMuted: boolean) => void;
  isMicMuted: boolean;
  setIsMicMuted: (muted: boolean) => void;

  // Voice chat state
  isVoiceChatLoading: boolean;
  setIsVoiceChatLoading: (loading: boolean) => void;
  isVoiceChatActive: boolean;
  setIsVoiceChatActive: (active: boolean) => void;

  // Session state
  sessionState: SessionState;
  setSessionState: (state: SessionState) => void;
  stream: MediaStream | null;
  setStream: (stream: MediaStream | null) => void;

  // Session close tracking
  sessionCloseReason: SessionCloseReason;
  setSessionCloseReason: (reason: SessionCloseReason) => void;

  // Message history
  messages: Message[];
  clearMessages: () => void;
  // Events have different structures now, using generic handler pattern or specific updates
  handleUserTalkingMessage: (text: string) => void; // Simplified
  handleStreamingTalkingMessage: (text: string) => void; // Simplified
  handleEndMessage: () => void;
  addMessage: (sender: MessageSender, content: string) => void;

  // Conversation state
  isListening: boolean;
  setIsListening: (listening: boolean) => void;
  isUserTalking: boolean;
  setIsUserTalking: (talking: boolean) => void;
  isAvatarTalking: boolean;
  setIsAvatarTalking: (talking: boolean) => void;

  // Connection quality
  connectionQuality: ConnectionQuality;
  setConnectionQuality: (quality: ConnectionQuality) => void;

  // Raw WebSocket URL for custom speaking events
  wsUrl: string | null;
  setWsUrl: (url: string | null) => void;
}

// Create context with defaults
const StreamingAvatarContext = createContext<StreamingAvatarContextProps>({
  avatarRef: { current: null },
  isMuted: true,
  setIsMuted: () => {},
  isMicMuted: false,
  setIsMicMuted: () => {},
  isVoiceChatLoading: false,
  setIsVoiceChatLoading: () => {},
  sessionState: SessionState.INACTIVE,
  setSessionState: () => {},
  isVoiceChatActive: false,
  setIsVoiceChatActive: () => {},
  stream: null,
  setStream: () => {},
  sessionCloseReason: null,
  setSessionCloseReason: () => {},
  messages: [],
  clearMessages: () => {},
  handleUserTalkingMessage: () => {},
  handleStreamingTalkingMessage: () => {},
  handleEndMessage: () => {},
  addMessage: () => {},
  isListening: false,
  setIsListening: () => {},
  isUserTalking: false,
  setIsUserTalking: () => {},
  isAvatarTalking: false,
  setIsAvatarTalking: () => {},
  connectionQuality: ConnectionQuality.UNKNOWN,
  setConnectionQuality: () => {},
  wsUrl: null,
  setWsUrl: () => {},
});

// Provider component
interface StreamingAvatarProviderProps {
  children: ReactNode;
  basePath?: string;
}

export const StreamingAvatarProvider: React.FC<StreamingAvatarProviderProps> = ({
  children,
  basePath,
}) => {
  // Avatar reference
  const avatarRef = useRef<LiveAvatarSession | null>(null);

  // Mute state
  const [isMuted, setIsMuted] = useState(true);
  const [isMicMuted, setIsMicMuted] = useState(false);

  // Voice chat state
  const [isVoiceChatLoading, setIsVoiceChatLoading] = useState(false);
  const [isVoiceChatActive, setIsVoiceChatActive] = useState(false);

  // Session state
  const [sessionState, setSessionState] = useState<SessionState>(SessionState.INACTIVE);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [wsUrl, setWsUrl] = useState<string | null>(null);

  // Session close reason tracking
  const [sessionCloseReason, setSessionCloseReason] = useState<SessionCloseReason>(null);

  // Message history
  const [messages, setMessages] = useState<Message[]>([]);
  const currentSenderRef = useRef<MessageSender | null>(null);

  // Conversation state
  const [isListening, setIsListening] = useState(false);
  const [isUserTalking, setIsUserTalking] = useState(false);
  const [isAvatarTalking, setIsAvatarTalking] = useState(false);

  // Connection quality
  const [connectionQuality, setConnectionQuality] = useState<ConnectionQuality>(
    ConnectionQuality.UNKNOWN,
  );

  // Message handlers - Updated to receive text directly
  const handleUserTalkingMessage = (text: string) => {
    if (currentSenderRef.current === MessageSender.CLIENT) {
      // Append to existing message
      setMessages((prev) => [
        ...prev.slice(0, -1),
        {
          ...prev[prev.length - 1],
          content: prev[prev.length - 1].content + text,
        },
      ]);
    } else {
      // New message
      currentSenderRef.current = MessageSender.CLIENT;
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now().toString(),
          sender: MessageSender.CLIENT,
          content: text,
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        },
      ]);
    }
  };

  const handleStreamingTalkingMessage = (text: string) => {
    if (currentSenderRef.current === MessageSender.AVATAR) {
      // Append to existing message
      setMessages((prev) => [
        ...prev.slice(0, -1),
        {
          ...prev[prev.length - 1],
          content: prev[prev.length - 1].content + text,
        },
      ]);
    } else {
      // New message
      currentSenderRef.current = MessageSender.AVATAR;
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now().toString(),
          sender: MessageSender.AVATAR,
          content: text,
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        },
      ]);
    }
  };

  const handleEndMessage = () => {
    currentSenderRef.current = null;
  };

  const clearMessages = () => {
    setMessages([]);
    currentSenderRef.current = null;
  };

  // Manual message addition (for RAG responses)
  const addMessage = (sender: MessageSender, content: string) => {
    setMessages((prev) => [
      ...prev,
      {
        id: Date.now().toString(),
        sender,
        content,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      },
    ]);
  };

  return (
    <StreamingAvatarContext.Provider
      value={{
        avatarRef,
        basePath,
        isMuted,
        setIsMuted,
        isMicMuted,
        setIsMicMuted,
        isVoiceChatLoading,
        setIsVoiceChatLoading,
        isVoiceChatActive,
        setIsVoiceChatActive,
        sessionState,
        setSessionState,
        stream,
        setStream,
        sessionCloseReason,
        setSessionCloseReason,
        messages,
        clearMessages,
        handleUserTalkingMessage,
        handleStreamingTalkingMessage,
        handleEndMessage,
        addMessage,
        isListening,
        setIsListening,
        isUserTalking,
        setIsUserTalking,
        isAvatarTalking,
        setIsAvatarTalking,
        connectionQuality,
        setConnectionQuality,
        wsUrl,
        setWsUrl,
      }}
    >
      {children}
    </StreamingAvatarContext.Provider>
  );
};

// Hook to use the context
export const useStreamingAvatarContext = () => {
  return useContext(StreamingAvatarContext);
};

export default StreamingAvatarContext;
